import { z } from 'zod';

import { OrderKind, OrderKindSchema, OrderStatusSchema, PaymentProviderSchema } from '../enums';
import { IdSchema, KopecksSchema } from './common';

/**
 * Заказы и платежи (BLUEPRINT Stage 7): публичный DTO заказа для Mini App,
 * входы `payments.createInvoice`/`payments.orderStatus` и `admin.orders.*`.
 */

/** Что можно купить через `payments.createInvoice`: сезон (Stage 7) и пробный сбор (Stage 11). */
export const InvoiceKindSchema = z.enum(['SEASON', 'TRIAL']);
export type InvoiceKind = z.infer<typeof InvoiceKindSchema>;

export const PaymentsCreateInvoiceInputSchema = z.object({
  kind: InvoiceKindSchema,
  /** SEASON → seasonId; TRIAL → gatheringId. Открытые места покупаются через `openSeats.claim` */
  targetId: IdSchema,
});
export type PaymentsCreateInvoiceInput = z.infer<typeof PaymentsCreateInvoiceInputSchema>;

export const OrderIdInputSchema = z.object({ orderId: IdSchema });

/** Разбивка цены — для экрана оплаты («цена сезона», «скидка капитана», «доля запасного»). */
export const OrderBreakdownSchema = z.object({
  listPriceKopecks: KopecksSchema,
  captainDiscountKopecks: KopecksSchema,
  /** Скидка продления сезона (Stage 12, задача 5) */
  renewalDiscountKopecks: KopecksSchema.default(0),
  trialCreditKopecks: KopecksSchema,
  /** Зачёт оплаченных разовых мест за `pricing.singleCreditDays` (Stage 17, ADR-35); у старых заказов нет */
  singleCreditKopecks: KopecksSchema.default(0),
  referralBonusKopecks: KopecksSchema,
});
export type OrderBreakdown = z.infer<typeof OrderBreakdownSchema>;

/**
 * Цена сезона для конкретного участника до создания счёта. Считается тем же
 * `computeSeasonPrice`, что и сам заказ: иначе экран называет одну сумму, а счёт выставляется на
 * другую — ровно это и происходило с капитаном, которому полагается скидка 50 %.
 */
export const SeasonPricePreviewSchema = OrderBreakdownSchema.extend({
  discountKopecks: KopecksSchema,
  amountKopecks: KopecksSchema,
  companyDiscountKopecks: KopecksSchema.default(0),
});
export type SeasonPricePreview = z.infer<typeof SeasonPricePreviewSchema>;

export const OrderPublicSchema = z.object({
  id: IdSchema,
  kind: OrderKindSchema,
  status: OrderStatusSchema,
  provider: PaymentProviderSchema,
  amountKopecks: KopecksSchema,
  discountKopecks: KopecksSchema,
  currency: z.string(),
  seasonId: IdSchema.nullable(),
  squadId: IdSchema.nullable(),
  gatheringId: IdSchema.nullable(),
  /** Ссылка счёта Telegram (`createInvoiceLink`), у ручного заказа — null */
  invoiceUrl: z.string().nullable(),
  breakdown: OrderBreakdownSchema.nullable(),
  paidAt: z.string().datetime().nullable(),
  refundedKopecks: KopecksSchema,
  createdAt: z.string().datetime(),
});
export type OrderPublic = z.infer<typeof OrderPublicSchema>;

/**
 * Почему заявка закрыта без оплаты — для экрана `/booking/:id` (Stage 17, задача 7) и строки
 * «Оплат и счетов» (раунд персон 3, N11). Живёт здесь, а не в `booking.ts`: её читает и
 * `MyPayment`, а `booking.ts` уже импортирует `order.ts` (обратный импорт дал бы цикл).
 */
export const BookingCloseKindSchema = z.enum([
  'hold_expired',
  'replaced',
  'ops_cancelled',
  /** Раунд персон 3 (N10): человек сам снял неоплаченную заявку */
  'user_cancelled',
]);
export type BookingCloseKind = z.infer<typeof BookingCloseKindSchema>;

/**
 * Строка «Оплат и счетов» (`payments.my`, раунд персон 1, N4): заказ с датой сбора. Заявка
 * (`BUNDLE`) — одной строкой, её позиции — внутри (`items`), отдельными строками не идут.
 */
export const MyPaymentItemSchema = OrderPublicSchema.extend({
  /** Начало сбора заказа (пробный, замена, разовое место); у сезона и заявки — null */
  gatheringStartsAt: z.string().datetime().nullable(),
  /** Таймзона города сбора */
  timezone: z.string().nullable(),
  activityName: z.string().nullable(),
  /** Деньги ждут возврата (выписка, невручённая позиция, поздний платёж) — «к возврату», а не «оплачено» */
  refundPending: z.boolean().default(false),
  /**
   * Раунд персон 3 (M5): до какого момента держится бронь неоплаченной заявки — «Оплаты» писали
   * «Ждёт оплаты» без срока. У заказа сезона брони нет — null
   */
  holdUntil: z.string().datetime().nullable().default(null),
  /**
   * Раунд персон 3 (N11): почему заявка закрыта без оплаты — «Отменено» четыре раза не говорило,
   * что бронь истекла и места вернулись. Не у заявки — null
   */
  closeReason: BookingCloseKindSchema.nullable().default(null),
});
export type MyPaymentItem = z.infer<typeof MyPaymentItemSchema>;

/**
 * Ссылка на заявку (раунд персон 2, N2/M8): куда дописана снятая бронь — следующая заявка того же
 * человека, собранная в той же транзакции, что сняла прежнюю.
 */
export const OrderRefSchema = z.object({
  id: IdSchema,
  createdAt: z.string().datetime(),
});
export type OrderRef = z.infer<typeof OrderRefSchema>;

export const MyPaymentSchema = MyPaymentItemSchema.extend({
  items: z.array(MyPaymentItemSchema),
  /** Бронь дописана в новую заявку («Объединена в заявку от …»); null — не дописывалась */
  mergedInto: OrderRefSchema.nullable().default(null),
});
export type MyPayment = z.infer<typeof MyPaymentSchema>;

/** Режим оплаты, который показывает экран `/pay/season/:id`. */
export const PaymentModeSchema = z.enum(['invoice', 'manual', 'free']);
export type PaymentMode = z.infer<typeof PaymentModeSchema>;

export const CreateInvoiceResultSchema = z.object({
  order: OrderPublicSchema,
  mode: PaymentModeSchema,
  /** `invoice` — ссылка счёта; `openWith` говорит, как открывать: `invoice` — `openInvoice` Telegram, `link` — внешний браузер (Робокасса) */
  invoiceUrl: z.string().nullable(),
  openWith: z.enum(['invoice', 'link']).nullable(),
  /** `manual` — реквизиты перевода из конфига (`PAYMENT_MANUAL_DETAILS`), null — обращаться в поддержку */
  manualDetails: z.string().nullable(),
  supportUsername: z.string().nullable(),
});
export type CreateInvoiceResult = z.infer<typeof CreateInvoiceResultSchema>;

// ───────────────────────────── админка ─────────────────────────────

export const AdminOrdersListInputSchema = z.object({
  status: z.array(OrderStatusSchema).max(6).optional(),
  provider: z.array(PaymentProviderSchema).max(3).optional(),
  kind: z.array(OrderKindSchema).max(Object.keys(OrderKind).length).optional(),
  seasonId: IdSchema.optional(),
  squadId: IdSchema.optional(),
  userId: IdSchema.optional(),
  /** Имя, username или Telegram ID плательщика */
  search: z.string().trim().max(60).optional(),
  /**
   * Stage 17, задача 6: только «ждут возврата» — пометка `refundPending` у заказа или у любой
   * позиции заявки (выписка, невручённая, поздний платёж, отменённый сбор; сбой возврата провайдера)
   */
  refundPending: z.boolean().optional(),
  cursor: IdSchema.optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
export type AdminOrdersListInput = z.infer<typeof AdminOrdersListInputSchema>;

export const AdminOrderMarkPaidInputSchema = z.object({
  orderId: IdSchema,
  /** Как пришли деньги: «СБП 03.09, 7 500 ₽» */
  comment: z.string().trim().max(300).nullable().default(null),
});
export type AdminOrderMarkPaidInput = z.infer<typeof AdminOrderMarkPaidInputSchema>;

export const AdminOrderRefundInputSchema = z.object({
  orderId: IdSchema,
  /** null — вернуть всё, что ещё не возвращено */
  amountKopecks: z.number().int().positive().nullable().default(null),
  reason: z.string().trim().min(2, 'Укажи причину').max(500),
  /**
   * Деньги вернули вне системы (перевод вручную, спор с провайдером): записать возврат,
   * не вызывая API провайдера. Для MANUAL-заказов всегда так.
   */
  external: z.boolean().default(false),
});
export type AdminOrderRefundInput = z.infer<typeof AdminOrderRefundInputSchema>;

export const AdminOrderCancelInputSchema = z.object({
  orderId: IdSchema,
  reason: z.string().trim().max(300).nullable().default(null),
});
export type AdminOrderCancelInput = z.infer<typeof AdminOrderCancelInputSchema>;
